import { useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  FilterType,
  PagedDataGrid,
  useMutation,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
  type IPagedDataGridRefType,
} from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import ConfirmDialog from '../../common/ConfirmDialog';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deleteBillingClass,
  getBillingClassesPaged,
  type IBillingClass,
} from '../../services/settingsService';

type BillingClassRow = IBillingClass & { id: number };

const columns: DataGridColumn<BillingClassRow>[] = [
  { key: 'code', name: 'Code', minWidth: 80, width: 140, isSortable: true },
  { key: 'name', name: 'Name', minWidth: 160, width: 280, isSortable: true },
  {
    key: 'description',
    name: 'Description',
    minWidth: 200,
    width: 420,
    isSortable: true,
    value: (item) => item.description ?? '',
  },
];

const filterColumns: IFilterableColumn[] = [
  { key: 'code', label: 'Code', type: FilterType.String },
  { key: 'name', label: 'Name', type: FilterType.String },
  { key: 'description', label: 'Description', type: FilterType.String },
];

const loadBillingClasses = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
) => {
  const page = await getBillingClassesPaged(
    abortController,
    pageSize,
    pageNo,
    orderBy,
    search,
    filters
  );
  return {
    ...page,
    items: page.items.filter(
      (row): row is BillingClassRow => typeof row.id === 'number'
    ),
  };
};

const BillingClasses = () => {
  const navigate = useNavigate();
  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selected, setSelected] = useState<BillingClassRow>();
  const [isDeleteOpen, { setTrue: showDelete, setFalse: hideDelete }] =
    useBoolean(false);
  const remove = useMutation(
    (row: BillingClassRow, abortController: AbortController) =>
      deleteBillingClass(abortController, row.id)
  );

  const commands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newBillingClass',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => {
          void navigate('/newBillingClass');
        },
        disabled: !isAdmin,
      },
      {
        key: 'editBillingClass',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selected) void navigate(`/editBillingClass?id=${selected.id}`);
        },
        disabled: !isAdmin || !selected,
      },
      {
        key: 'deleteBillingClass',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: showDelete,
        disabled: !isAdmin || !selected || remove.loading,
      },
    ],
    [isAdmin, navigate, selected, showDelete, remove.loading]
  );

  const confirmDelete = async () => {
    hideDelete();
    if (!selected) return;
    const outcome = await remove.mutate(selected);
    if (outcome.status === 'success') {
      gridRef.current?.refresh();
      setSuccessMessage(`Billing class ${selected.code} deleted.`);
      setSelected(undefined);
    }
    if (outcome.status === 'error') {
      setErrorMessage(
        getApiErrorMessage(outcome.error, 'Failed to delete billing class.')
      );
    }
  };

  return (
    <>
      <PagedDataGrid<BillingClassRow>
        ref={gridRef}
        columns={columns}
        getAction={loadBillingClasses}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        filterEnabled
        filterColumns={filterColumns}
        empty={{
          title: 'No billing classes yet!',
          action: isAdmin
            ? {
                text: 'Add billing class',
                onClick: () => {
                  void navigate('/newBillingClass');
                },
              }
            : undefined,
        }}
      />
      <ConfirmDialog
        isModalOpen={isDeleteOpen}
        hideModal={hideDelete}
        message={`Billing class ${
          selected?.code ?? ''
        } would be deleted, are you sure?`}
        onYesClick={confirmDelete}
      />
    </>
  );
};

export default BillingClasses;
